import { create } from "zustand";
import { User, onAuthStateChanged, signInWithPopup, signOut as fbSignOut } from "firebase/auth";
import { auth, googleProvider } from "@/lib/firebase";
import { DEFAULT_ORG_ID, setCurrentOrgId } from "@/lib/org";
import { subscribeOrg, unsubscribeOrg } from "@/store/org";

export type Role = "admin" | "driver" | null;

interface AuthState {
  user: User | null;
  role: Role;
  orgId: string; // claims.orgId ?? DEFAULT_ORG_ID (3단계 호환 전환의 B단계 폴백)
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  init: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  role: null,
  orgId: DEFAULT_ORG_ID,
  loading: true,
  init: () => {
    onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setCurrentOrgId(null);
        unsubscribeOrg();
        set({ user: null, role: null, orgId: DEFAULT_ORG_ID, loading: false });
        return;
      }
      let token = await u.getIdTokenResult();
      // 권한 정보가 없으면 한 번 새로 받는다 — 업체를 만든 직후 앱을 다시 열면 옛 토큰(최대 1시간) 때문에 환영 화면이 또 뜨던 문제(3차 예행연습 5번)
      if (!token.claims.role) {
        try { token = await u.getIdTokenResult(true); } catch { /* 인터넷이 없으면 옛 정보로 간다 */ }
      }
      const orgId = (token.claims.orgId as string) || DEFAULT_ORG_ID;
      setCurrentOrgId(orgId); // 쿼리 헬퍼(org.ts)보다 먼저 — 이후 모든 org 스코프 쿼리의 기준
      // 역할 없는 계정(초대 전·환영 화면)은 업체 설정을 구독하지 않는다 — 규칙도 막지만, 남의 업체 설정을 애초에 요청하지 않는다
      if (token.claims.role) subscribeOrg(orgId); // 창고·권역 등 org 설정 실시간 로드
      else unsubscribeOrg();
      set({
        user: u,
        role: (token.claims.role as Role) ?? null,
        orgId,
        loading: false,
      });
    });
  },
  signIn: async () => {
    await signInWithPopup(auth, googleProvider);
  },
  signOut: async () => {
    await fbSignOut(auth);
  },
}));
